import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Footer } from '../../components/Footer';
import { ArrowLeft, CheckCircle, MapPin, Phone } from 'lucide-react';

const locations = [
  'Scarsdale', 'Greenwich', 'Rye', 'Harrison', 'Mamaroneck', 'Bedford',
  'Chappaqua', 'Bronxville', 'Larchmont', 'Purchase'
];

const features = [
  {
    title: 'Built-in Cabinets',
    description: 'Custom cabinetry designed and built to your specifications.'
  },
  {
    title: 'Custom Shelving',
    description: 'Personalized storage solutions for any room.'
  },
  {
    title: 'Trim & Molding',
    description: 'Decorative woodwork to enhance your space.'
  },
  {
    title: 'Door Installation',
    description: 'Custom door creation and professional installation.'
  },
  {
    title: 'Wood Repairs',
    description: 'Expert repair of wooden elements and structures.'
  },
  {
    title: 'Furniture Building',
    description: 'Custom furniture pieces built to your specifications.'
  }
];

const portfolioImages = [
  {
    url: 'https://images.unsplash.com/photo-1609280069678-ab9ef26a0b05?q=80&w=2358&auto=format&fit=crop',
    location: 'Scarsdale Built-ins'
  },
  {
    url: 'https://images.unsplash.com/photo-1609280069678-ab9ef26a0b05?q=80&w=2358&auto=format&fit=crop',
    location: 'Greenwich Custom Shelving'
  },
  {
    url: 'https://images.unsplash.com/photo-1609280069678-ab9ef26a0b05?q=80&w=2358&auto=format&fit=crop',
    location: 'Rye Custom Cabinets'
  }
];

export function CustomCarpentryPage() {
  return (
    <div className="min-h-screen bg-zinc-900">
      <Helmet>
        <title>Custom Carpentry Services in Westchester County | Golden Touch</title>
        <meta name="description" content="Expert carpentry services in Westchester County & Greenwich. From built-in cabinets to custom furniture, we bring quality craftsmanship to every project." />
        <meta name="keywords" content="custom carpentry Westchester, cabinet maker Greenwich, custom woodwork Scarsdale, built-in cabinets" />
        <link rel="canonical" href="https://renovationwestchester.com/services/custom-carpentry" />
      </Helmet>


      <main className="pt-24">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <Link to="/services" className="inline-flex items-center gap-2 text-yellow-400 mb-12 hover:gap-4 transition-all duration-300">
            <ArrowLeft className="h-4 w-4" />
            <span className="text-sm tracking-wider">Back to Services</span>
          </Link>

          <div className="grid md:grid-cols-2 gap-16 items-center mb-24">
            <div>
              <h1 className="text-4xl md:text-6xl font-light text-zinc-100 mb-6">
                Custom Carpentry Services in Westchester
              </h1>
              <p className="text-zinc-400 mb-8 leading-relaxed">
                From built-in cabinets to custom furniture, our skilled craftsmen bring expertise 
                and attention to detail to every project. We create beautiful, functional pieces 
                that enhance your home.
              </p>
              <div className="flex items-center gap-2 text-yellow-400 mb-8">
                <MapPin className="h-5 w-5" />
                <span className="text-sm tracking-wider">Westchester County & Greenwich Area</span>
              </div>
              <Link 
                to="/consultation"
                className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
              >
                <Phone className="h-4 w-4" />
                <span className="text-sm tracking-wider">Schedule a Consultation</span>
              </Link>
            </div>
            <div className="aspect-[4/3] overflow-hidden">
              <img 
                src={portfolioImages[0].url}
                alt="Custom Carpentry in Westchester"
                className="w-full h-full object-cover"
                width="1200"
                height="900"
              />
            </div>
          </div>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Our Services</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, index) => (
                <div key={index} className="bg-zinc-800/30 p-8">
                  <CheckCircle className="h-8 w-8 text-yellow-400 mb-4" />
                  <h3 className="text-xl text-zinc-100 mb-4">{feature.title}</h3>
                  <p className="text-zinc-400">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          <section className="mb-24">
            <h2 className="text-3xl font-light text-zinc-100 mb-12">Areas Served</h2>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {locations.map((location) => (
                <Link
                  key={location}
                  to={`/areas/${location.toLowerCase()}`}
                  className="block bg-zinc-800/30 p-4 text-center border border-transparent hover:border-yellow-400/40 transition-colors"
                >
                  <MapPin className="h-5 w-5 text-yellow-400 mx-auto mb-2" />
                  <span className="text-zinc-300">{location}</span>
                </Link>
              ))}
            </div>
          </section>

          <section className="text-center">
            <h2 className="text-3xl font-light text-zinc-100 mb-8">Ready to Start Your Custom Project?</h2>
            <p className="text-zinc-400 mb-8 max-w-2xl mx-auto">
              Let's discuss your carpentry needs. Our skilled craftsmen are ready to create 
              beautiful, functional pieces that enhance your home.
            </p>
            <Link 
              to="/consultation"
              className="inline-flex items-center gap-4 px-8 py-4 bg-yellow-400 text-zinc-900"
            >
              <Phone className="h-4 w-4" />
              <span className="text-sm tracking-wider">Schedule Your Consultation</span>
            </Link>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}